import { FetchClient, storeClient } from '@/shared/api/client';
import { SeoListItem, SeoResponse } from '@/shared/api/types';
import { OrderBy } from '@/shared/types/order-by';

import {
  CrossRewriteCategoryResponse,
  FilterListResponse,
  ProductListRedirectResponse,
  ProductListResponse,
  PromotionsSelloutListResponse,
} from './types';

export const fetchProductsRedirect = async (
  categoryId: string,
  fetch?: FetchClient
): Promise<ProductListRedirectResponse> => {
  try {
    return await storeClient<ProductListRedirectResponse>(
      `/category-redirect/get/${categoryId}`,
      {},
      fetch
    ).catch((error) => {
      return {
        redirectTo: null,
      };
    });
  } catch (error) {
    return {
      redirectTo: null,
    };
  }
};

export const fetchProducts = async (
  categoryId: string,
  page: number = 1,
  limit: number = 50,
  ascending: number = 1,
  query: string = '',
  filters: Record<string, unknown> = {},
  orderBy: OrderBy = 'sales_ratio',
  fetch?: FetchClient
): Promise<ProductListResponse> => {
  try {
    const result = await storeClient<ProductListResponse>(
      `/product/list-grid/${categoryId}`,
      {
        queryParams: {
          page,
          limit,
          ascending,
          query,
          filters,
          orderBy: orderBy === 'sales_ratio' ? '' : orderBy,
        },
      },
      fetch
    );

    return result;
  } catch {
    return {
      count: -1,
      data: [],
      defaultCalendar: {},
      localeCalendar: {},
      shippingDeliverySettings: {
        isCutoff: 0,
        immediateShippingDelay: 0,
        logisticDelay: 0,
      },
      breadcrumbs: [],
    };
  }
};

export const fetchFilterList = async (
  categoryId: string,
  filters: Record<string, unknown> = {},
  fetch?: FetchClient
): Promise<FilterListResponse> => {
  try {
    const result = await storeClient<FilterListResponse>(
      `/category/filter-list/${categoryId}`,
      {
        queryParams: {
          filters,
        },
      },
      fetch
    );

    return result;
  } catch {
    return [];
  }
};

export const fetchSearch = async (
  query: string,
  page: number = 1,
  limit: number = 50,
  ascending: number = 1,
  orderBy: 'name' | 'price' = 'name',
  fetch?: FetchClient
): Promise<ProductListResponse> => {
  try {
    const result = await storeClient<ProductListResponse>(
      `/search/list-grid`,
      {
        queryParams: {
          query,
          page,
          limit,
          ascending,
          orderBy,
        },
      },
      fetch
    );

    return result;
  } catch {
    return {
      count: -1,
      data: [],
      defaultCalendar: {},
      localeCalendar: {},
      shippingDeliverySettings: {
        isCutoff: 0,
        immediateShippingDelay: 0,
        logisticDelay: 0,
      },
      breadcrumbs: [],
    };
  }
};

export const fetchProductListCategorySeo = async (
  category: string,
  fetch?: FetchClient
): Promise<SeoListItem> => {
  const initialResult = {
    bottomDescription: '',
    canonical: '',
    locale: '',
    meta: {
      description: '',
      title: '',
      canonical: null,
    },
    robots: '',
    topDescription: {
      h1: '',
      popularCollections: [],
    },
    url: '',
  };

  try {
    const result = await storeClient<SeoListItem>(
      category.includes('/')
        ? `/category-rewrite/seo/${category}`
        : `/category/seo/${category}`,
      {},
      fetch
    );

    return Array.isArray(result) ? initialResult : result;
  } catch (e) {
    return initialResult;
  }
};

export const fetchProductListSearchSeo = async (
  query: string,
  fetch?: FetchClient
): Promise<SeoResponse> => {
  const initialResult = {
    locale: '',
    canonical: '',
    robots: '',
    meta: {
      description: '',
      title: '',
      canonical: null,
    },
  };

  try {
    const result = await storeClient<SeoResponse>(
      `/search/seo`,
      {
        queryParams: {
          query,
        },
      },
      fetch
    );

    return Array.isArray(result) ? initialResult : result;
  } catch (e) {
    return initialResult;
  }
};

export const fetchCrossRewriteCategory = async (
  slug: string,
  fetch?: FetchClient
): Promise<CrossRewriteCategoryResponse> => {
  try {
    const result = await storeClient<CrossRewriteCategoryResponse>(
      `/category/cross-rewrite/${slug}`,
      {},
      fetch
    );

    if (!Array.isArray(result)) {
      return [];
    }

    return result;
  } catch (e) {
    return [];
  }
};

export const fetchPromotions = async (
  page: number = 1,
  limit: number = 50,
  ascending: number = 1,
  query: string = '',
  filters: Record<string, unknown> = {},
  orderBy: OrderBy = 'sales_ratio',
  fetch?: FetchClient
): Promise<PromotionsSelloutListResponse> => {
  try {
    const result = await storeClient<PromotionsSelloutListResponse>(
      '/promotion/list-grid',
      {
        queryParams: {
          page,
          limit,
          ascending,
          query,
          filters,
          orderBy: orderBy === 'sales_ratio' ? '' : orderBy,
        },
      },
      fetch
    );

    return result;
  } catch {
    return {
      count: -1,
      data: [],
      defaultCalendar: {},
      localeCalendar: {},
      shippingDeliverySettings: {
        isCutoff: 0,
        immediateShippingDelay: 0,
        logisticDelay: 0,
      },
      breadcrumbs: { name: '', slug: '' },
    };
  }
};

export const fetchSellouts = async (
  page: number = 1,
  limit: number = 50,
  ascending: number = 1,
  query: string = '',
  filters: Record<string, unknown> = {},
  orderBy: OrderBy = 'sales_ratio',
  fetch?: FetchClient
): Promise<PromotionsSelloutListResponse> => {
  try {
    const result = await storeClient<PromotionsSelloutListResponse>(
      '/sellout/list-grid',
      {
        queryParams: {
          page,
          limit,
          ascending,
          query,
          filters,
          orderBy: orderBy === 'sales_ratio' ? '' : orderBy,
        },
      },
      fetch
    );

    return result;
  } catch {
    return {
      count: -1,
      data: [],
      defaultCalendar: {},
      localeCalendar: {},
      shippingDeliverySettings: {
        isCutoff: 0,
        immediateShippingDelay: 0,
        logisticDelay: 0,
      },
      breadcrumbs: { name: '', slug: '' },
    };
  }
};

export const fetchPromotionsFiltersList = async (
  filters: Record<string, unknown> = {},
  fetch?: FetchClient
): Promise<FilterListResponse> => {
  try {
    const result = await storeClient<FilterListResponse>(
      '/promotion/filter-list',
      {
        queryParams: {
          filters,
        },
      },
      fetch
    );

    return result;
  } catch {
    return [];
  }
};

export const fetchSelloutsFiltersList = async (
  filters: Record<string, unknown> = {},
  fetch?: FetchClient
): Promise<FilterListResponse> => {
  try {
    const result = await storeClient<FilterListResponse>(
      '/sellout/filter-list',
      {
        queryParams: {
          filters,
        },
      },
      fetch
    );

    return result;
  } catch {
    return [];
  }
};
